import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppBar, Avatar, Box, Button, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Toolbar, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Celebration as CelebrationIcon, Dashboard as DashboardIcon, Lock as LockIcon, Logout as LogoutIcon, Menu as MenuIcon, School as SchoolIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTeacherAuth } from '../hooks/useTeacherAuth';
import LanguageSelector from './LanguageSelector';
import { presentationBackground } from '../theme/presentationBackground';
import LittleLensLogo from './LittleLensLogo';

const drawerWidth = 260;

const TeacherLayout: React.FC<{ children: React.ReactNode; title?: string }> = ({ children, title }) => {
  const { t } = useTranslation('auth', { keyPrefix: 'portalLayout' });
  const [open, setOpen] = useState(false);
  const { teacher, logout } = useTeacherAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const nav = [
    { label: t('dashboard'), path: '/teacher/dashboard', icon: <DashboardIcon /> },
    { label: t('events'), path: '/teacher/events', icon: <CelebrationIcon /> },
    { label: t('changePassword'), path: '/teacher/change-password', icon: <LockIcon /> },
  ];

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#ffffff' }}>
      <Box sx={{ p: 2.5, pb: 2, color: 'white', background: 'linear-gradient(135deg, #2563eb 0%, #0891b2 100%)' }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
          <LittleLensLogo alt="LittleLens" maxWidth={100} />
        </Box>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          p: 1.5, 
          borderRadius: 2, 
          bgcolor: 'rgba(255,255,255,0.12)',
          backdropFilter: 'blur(8px)',
        }}>
          <Avatar sx={{ mr: 1.5, bgcolor: 'rgba(255,255,255,0.25)', width: 36, height: 36 }}><SchoolIcon fontSize="small" /></Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ color: 'white', lineHeight: 1.3 }}>{teacher?.name || t('teacherFallback')}</Typography>
            <Typography variant="caption" noWrap sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.7rem' }}>{teacher?.email}</Typography>
          </Box>
        </Box>
      </Box>
      <List sx={{ p: 1.5, flex: 1 }}>
        {nav.map((item) => (
          <ListItemButton key={item.path} selected={location.pathname === item.path} onClick={() => { navigate(item.path); setOpen(false); }} sx={{ py: 1, mb: 0.25 }}>
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: location.pathname === item.path ? 700 : 500 }} />
          </ListItemButton>
        ))}
      </List>
      <Box sx={{ p: 1.5 }}>
        <Button color="error" variant="text" startIcon={<LogoutIcon />} fullWidth onClick={() => { logout(); navigate('/teacher/login'); }} sx={{ justifyContent: 'flex-start', px: 1.5 }}>
          {t('logout')}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'transparent' }}>
      <Drawer variant="permanent" sx={{ display: { xs: 'none', md: 'block' }, width: drawerWidth, '& .MuiDrawer-paper': { width: drawerWidth, border: 0, boxShadow: '0 4px 24px rgba(15,23,42,0.06)' } }}>{drawer}</Drawer>
      <Drawer variant="temporary" open={open} onClose={() => setOpen(false)} sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: drawerWidth } }}>{drawer}</Drawer>
      <Box sx={{ flex: 1, width: { md: `calc(100% - ${drawerWidth}px)` } }}>
        {isMobile ? (
          <AppBar position="fixed" sx={{ background: 'linear-gradient(135deg, #2563eb 0%, #0891b2 100%)', boxShadow: '0 2px 12px rgba(37,99,235,0.2)' }}>
            <Toolbar sx={{ minHeight: { xs: 56 } }}>
              <IconButton color="inherit" edge="start" onClick={() => setOpen(true)} sx={{ mr: 1.5 }}><MenuIcon /></IconButton>
              <Typography variant="h6" noWrap sx={{ flexGrow: 1, fontWeight: 700, fontSize: '1.05rem' }}>{title || t('teacherPortal')}</Typography>
              <LanguageSelector />
            </Toolbar>
          </AppBar>
        ) : (
          <AppBar position="fixed" sx={{ width: `calc(100% - ${drawerWidth}px)`, ml: `${drawerWidth}px`, background: presentationBackground.sidebarGradient, color: '#ffffff', boxShadow: '0 2px 12px rgba(38,155,178,0.25)', borderBottom: 'none', backdropFilter: 'none' }}>
            <Toolbar sx={{ justifyContent: 'flex-end', minHeight: { md: 56 } }}>
              <LanguageSelector />
            </Toolbar>
          </AppBar>
        )}
        <Box component="main" className="ll-page-content" sx={{ p: { xs: 2, sm: 3, md: 3.5 }, mt: 7 }}>{children}</Box>
      </Box>
    </Box>
  );
};

export default TeacherLayout;
